import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faReply,
  faRotate,
  faCircleCheck,
  faFileCode,
  faFileAudio,
  faPause,
  faPlay,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

import dragImg from '@Assets/images/tutorial/drag.png';
import swapGroupImg from '@Assets/images/tutorial/swap_group.png';
import swapOneImg from '@Assets/images/tutorial/swap_one.png';
import altFingeringOffImg from '@Assets/images/tutorial/altFg_toggle_off.png';
import altFingeringOnImg from '@Assets/images/tutorial/altFg_toggle_on.png';
import pitchSharpFlatOffImg from '@Assets/images/tutorial/pitch_sharpflat_toggle_off.png';
import sharpFlatOnImg from '@Assets/images/tutorial/sharpflat_toggle_on.png';
import pitchOnImg from '@Assets/images/tutorial/pitch_toggle_on.png';
import fontSizeSmallImg from '@Assets/images/tutorial/fontsize_small.png';
import fontSizeMiddleImg from '@Assets/images/tutorial/fontsize_middle.png';
import fontSizeLargeImg from '@Assets/images/tutorial/fontsize_large.png';

/** A small icon + line of copy shown inside a step card body (used for
 *  the edit-mode Undo / Reset / Done controls, which only render in the
 *  toolbar while editing and so can't be spotlighted directly). */
export interface TutorialBullet {
  icon: IconDefinition;
  label: string;
  text: string;
}

export interface TutorialStep {
  id: string;
  /** Matches the `data-tour="…"` attribute on the highlighted control.
   *  Steps whose target element isn't in the DOM are skipped at runtime. */
  target: string;
  title: string;
  description: string;
  /** Optional plain (icon-less) bullet points shown right under the
   *  description — e.g. the drag / tap / hold actions for the edit step. */
  actions?: string[];
  /** Optional lead-in line shown above the icon `bullets` list. */
  bulletsIntro?: string;
  /** Optional sub-points with icons (e.g. Undo / Reset / Done for the
   *  edit step). */
  bullets?: TutorialBullet[];
  /** Optional small image/GIF/video shown in the card. When omitted the
   *  card renders a neutral placeholder box so real media can be dropped
   *  in later without touching the component. `media` may be a PNG/SVG/GIF
   *  (rendered as <img>) or an .mp4/.webm (rendered as autoplaying <video>). */
  media?: string;
  mediaAlt?: string;
  /** Optional ordered list of images shown stacked vertically with a
   *  vertical "toggle" arrow between each consecutive pair. Takes
   *  precedence over `media`. Two entries illustrate an off → on toggle;
   *  three (e.g. font size small/medium/large) show a progression. */
  mediaSequence?: Array<{ src: string; alt?: string }>;
  /** Optional images laid out left-to-right in a single row (e.g. the
   *  drag / swap-group / swap-one actions for the edit step). Takes
   *  precedence over `media`/`mediaSequence`. */
  mediaRow?: Array<{ src: string; alt?: string }>;
}

/**
 * Onboarding steps, in tour order. Engraved XML projects add Playback and
 * MIDI; steps whose control is not on the page are skipped.
 */
export type TutorialPage = 'pdf' | 'xml';

const PITCH_LEDGER =
  'Choose note names on every note, only on notes with two or more ledger lines, or none.';

/** Scan projects use the on/off pitch toggle. Engraved XML projects use modes. */
export const projectTutorialSteps = (
  page: TutorialPage = 'pdf'
): TutorialStep[] =>
  PROJECT_TUTORIAL_STEPS.map((step) => {
    if (page === 'xml' && step.id === 'pitch') {
      return { ...step, description: PITCH_LEDGER };
    }
    if (page === 'xml' && step.id === 'fontsize') {
      return {
        ...step,
        title: 'Fingering and pitch size',
        description:
          'Make the fingering numbers and pitch letters larger or smaller to meet your reading preference.',
      };
    }
    if (page === 'xml' && step.id === 'download-xml' && step.bullets) {
      // The XML page only offers the fingered exports.
      return {
        ...step,
        bullets: step.bullets.filter((bullet) => bullet.label !== 'MusicXML'),
      };
    }
    return step;
  });

export const PROJECT_TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'sharps',
    target: 'sharps',
    title: 'Sharps & Flats',
    description:
      'Toggle to highlight notes affected by sharps and flats with a different notehead color.',
    mediaSequence: [
      {
        src: pitchSharpFlatOffImg,
        alt: 'Score before sharps and flats are highlighted',
      },
      {
        src: sharpFlatOnImg,
        alt: 'Noteheads affected by sharps and flats highlighted in color',
      },
    ],
  },
  {
    id: 'fingerings',
    target: 'fingerings',
    title: 'Piano fingering',
    description:
      'Toggle to show or hide piano fingerings on the score. Turn this off to see the score without fingering numbers.',
    media: altFingeringOffImg,
    mediaAlt: 'Score with piano fingerings shown',
  },
  {
    id: 'alt',
    target: 'alt',
    title: 'Alternate fingerings',
    description:
      'Toggle on to show suggested alternative fingerings (lighter blue) next to the main ones.',
    mediaSequence: [
      {
        src: altFingeringOffImg,
        alt: 'Score with alternate fingerings hidden',
      },
      { src: altFingeringOnImg, alt: 'Score with alternate fingerings shown' },
    ],
  },
  {
    id: 'edit',
    target: 'edit',
    title: 'Edit fingerings',
    description: 'Tap the pencil to enter edit mode:',
    mediaRow: [
      { src: dragImg, alt: 'Drag a main number up or down' },
      { src: swapGroupImg, alt: 'Tap an alternative to swap the group' },
      { src: swapOneImg, alt: 'Hold an alternative to swap one' },
    ],
    actions: [
      'Drag a main number up or down to change it.',
      'Tap an alternative to swap the group in.',
      'Hold an alternative to swap individually.',
    ],
    bulletsIntro: 'While editing, these controls appear in the toolbar:',
    bullets: [
      {
        icon: faRotate,
        label: 'Reset',
        text: 'Reverts all changes since you entered edit mode.',
      },
      {
        icon: faReply,
        label: 'Undo',
        text: 'Reverts your last change.',
      },
      {
        icon: faCircleCheck,
        label: 'Done',
        text: 'Saves your edits and exits edit mode. Tapping the pencil again does the same.',
      },
    ],
  },
  {
    id: 'playback',
    target: 'playback',
    title: 'Playback',
    description:
      'Toggle to hear the engraved score. Tap a note to choose where playback starts.',
    bulletsIntro: 'While playback is on, these controls appear in the toolbar:',
    bullets: [
      {
        icon: faXmark,
        label: 'Exit',
        text: 'Leaves playback and returns to the score.',
      },
      {
        icon: faPlay,
        label: 'Play',
        text: 'Starts from the cursor.',
      },
      {
        icon: faPause,
        label: 'Pause',
        text: 'Stops and leaves the cursor where it is.',
      },
    ],
  },
  {
    id: 'pitch',
    target: 'pitch',
    title: 'Pitch',
    description:
      'Toggle to show the note name on each notehead to help you connect the fingering to the pitch.',
    mediaSequence: [
      { src: pitchSharpFlatOffImg, alt: 'Score with pitch hidden' },
      { src: pitchOnImg, alt: 'Score with note names shown' },
    ],
  },
  {
    id: 'fontsize',
    target: 'fontsize',
    title: 'Fingering size',
    description:
      'Make the fingering numbers larger or smaller to meet your reading preference.',
    mediaSequence: [
      { src: fontSizeSmallImg, alt: 'Small fingering numbers' },
      { src: fontSizeMiddleImg, alt: 'Medium fingering numbers' },
      { src: fontSizeLargeImg, alt: 'Large fingering numbers' },
    ],
  },
  {
    id: 'download-pdf',
    target: 'download-pdf',
    title: 'Download PDF',
    description:
      'The PDF button downloads a printable PDF of the score as you see it.',
  },
  {
    id: 'download-xml',
    target: 'download-xml',
    title: 'Download MusicXML',
    description: 'The XML button offers:',
    bullets: [
      {
        icon: faFileCode,
        label: 'MusicXML',
        text: 'The score without fingering numbers, including digital PDFs.',
      },
      {
        icon: faFileCode,
        label: 'MusicXML with Fingerings',
        text: 'MusicXML with the fingering numbers included.',
      },
      {
        icon: faFileCode,
        label: 'MusicXML with Fingerings (Sibelius)',
        text: 'The fingered file in a form Sibelius can open.',
      },
    ],
  },
  {
    id: 'midi',
    target: 'midi',
    title: 'Download MIDI',
    description: 'Export this score as a MIDI file:',
    bullets: [
      {
        icon: faFileAudio,
        label: 'MIDI',
        text: 'A MIDI file of the engraved score, to open in another music app.',
      },
    ],
  },
  {
    id: 'help',
    target: 'help',
    title: 'Need a refresher?',
    description:
      'Tap the question mark anytime to replay this tutorial and revisit what each control does.',
  },
];
